import { useRef, useContext, useCallback } from 'react';
import { CONVERSATION_CONSTANTS } from './src-config-miaw-constants.js';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';
import { subscribeToEventSource, closeEventSource } from './src-utils-miaw-event-source-utils.js';
import { getConversationEventMessage } from './src-utils-component-utils.js';
import { CAContext } from './src-contexts-CAContext.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';
import { handleLink } from './src-utils-miaw-agent-messages-utils.js';
import { setIsSendRoutingAttributes } from './src-utils-session-storage-utils.js';

const useMIAWAgentActions = () => {
  const { logCustomAttributesNR } = useNRLogging();
  const prevUnreadMessagesRef = useRef(0);
  const { setState } = useContext(MiawChatContext);
  const {
    setChatState,
    chatState: { chatWindow, playSoundMessageNotif }
  } = useContext(CAContext);
  const addEventListeners = useCallback(() => {
    subscribeToEventSource({
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_MESSAGE]: handleMessageEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_ROUTING_RESULT]: handleRoutingEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_PARTICIPANT_CHANGED]: handleParticipantChangedEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STARTED_INDICATOR]: handleTypingIndicatorEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STOPPED_INDICATOR]: handleTypingIndicatorEvent
      // [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_DELIVERY_ACKNOWLEDGEMENT]: (event) => { console.log('ACK EVENT', { event }); },
      // [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_READ_ACKNOWLEDGEMENT]: (event) => { console.log('READ EVENT', { event }); },
      // [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_CLOSE_CONVERSATION]: (event) => { console.log('CLOSE EVENT', { event }); },
    });
  }, []);
  const handleCloseEventSource = useCallback(() => {
    closeEventSource();
  }, []);
  const handleMessageEvent = useCallback(
    (event) => {
      const { data } = event;
      const parsedData = JSON.parse(data);
      const parsedPayload = JSON.parse(parsedData.conversationEntry.entryPayload);
      const staticContent = parsedPayload?.abstractMessage?.staticContent;
      let urlMapping = void 0;
      if (parsedPayload.abstractMessage.staticContent.formatType === "RichLink") {
        const linkData = handleLink(parsedPayload);
        if (linkData) urlMapping = linkData;
      }
      const messageList = [];
      if (staticContent.text) {
        messageList.push(staticContent.text);
      }
      if (urlMapping?.url) {
        messageList.push(urlMapping.url);
      }
      setState((prev) => ({
        ...prev,
        conversationMessages: [
          ...prev.conversationMessages,
          {
            id: parsedData.conversationEntry.identifier,
            messageContent: messageList.length > 0 ? messageList : [""],
            senderDisplayName: parsedData.conversationEntry.senderDisplayName,
            timestamp: parsedData.conversationEntry.clientTimestamp,
            type: parsedData.conversationEntry.sender.role === "EndUser" ? "user" : "agent",
            formatType: staticContent.formatType,
            attachments: staticContent.formatType === "Attachments" ? staticContent.attachments : void 0,
            isMessageRead: false,
            showFeedback: false,
            urlMapping: [...urlMapping ? [urlMapping] : []]
          }
        ]
      }));
      if (parsedData.conversationEntry.sender.role !== "EndUser") {
        setChatState((prev) => {
          const newUnreadCount = prev.unreadMessages ? prev.unreadMessages + 1 : 1;
          const shouldPlayAudio = newUnreadCount > prevUnreadMessagesRef.current && newUnreadCount > 0 && (document.hidden || chatWindow === "minimized");
          if (shouldPlayAudio) {
            playSoundMessageNotif?.();
          }
          prevUnreadMessagesRef.current = newUnreadCount;
          return {
            ...prev,
            unreadMessages: newUnreadCount
          };
        });
      }
    },
    [setChatState, setState]
  );
  const handleRoutingEvent = (event) => {
    const { data } = event;
    const parsedData = JSON.parse(data);
    setState((prev) => ({
      ...prev,
      conversationMessages: [
        ...prev.conversationMessages,
        {
          id: parsedData.conversationEntry.identifier,
          messageContent: "A specialist will be with you soon",
          //   senderDisplayName: parsedData.conversationEntry.senderDisplayName,
          timestamp: parsedData.conversationEntry.clientTimestamp,
          type: "system",
          showFeedback: false
        }
      ]
    }));
  };
  const handleParticipantChangedEvent = (event) => {
    const { data } = event;
    const parsedData = JSON.parse(data);
    const parsedPayload = JSON.parse(parsedData.conversationEntry.entryPayload);
    const sendRoutingDataWithMessage = parsedPayload.entries[0].operation === "remove" ? true : false;
    if (sendRoutingDataWithMessage) setIsSendRoutingAttributes(true);
    setState((prev) => ({
      ...prev,
      conversationMessages: [
        ...prev.conversationMessages,
        {
          id: parsedData.conversationEntry.identifier,
          messageContent: getConversationEventMessage(parsedPayload.entries[0].operation, parsedPayload.entries[0].displayName),
          //   senderDisplayName: parsedData.conversationEntry.senderDisplayName,
          timestamp: parsedData.conversationEntry.clientTimestamp,
          type: "system",
          showFeedback: false
        }
      ],
      agentName: parsedPayload.entries?.[0]?.operation === "add" ? parsedPayload.entries?.[0]?.displayName : "",
      sendRoutingDataWithMessage
    }));
  };
  const handleTypingIndicatorEvent = (event) => {
    const { data } = event;
    const parsedData = JSON.parse(data);
    const parsedPayload = JSON.parse(parsedData.conversationEntry.entryPayload);
    if (parsedData.conversationEntry.sender.role !== "EndUser") {
      if (parsedPayload.entryType === "TypingStartedIndicator") {
        setState((prev) => {
          if (prev.conversationMessages.some((message) => message.type === "Typing")) {
            return prev;
          }
          return {
            ...prev,
            conversationMessages: [
              ...prev.conversationMessages,
              {
                id: parsedData.conversationEntry.identifier,
                messageContent: "typing",
                senderDisplayName: parsedData.conversationEntry.senderDisplayName,
                timestamp: parsedData.conversationEntry.clientTimestamp,
                type: "Typing",
                showFeedback: false
              }
            ]
          };
        });
      } else if (parsedPayload.entryType === "TypingStoppedIndicator") {
        setState((prev) => ({
          ...prev,
          conversationMessages: prev.conversationMessages.filter((message) => message.type !== "Typing")
        }));
      }
    }
  };
  const subscribeToAgentActions = useCallback(() => {
    try {
      console.warn("[Chat-Anywhere] SF connection established.");
      addEventListeners();
    } catch (error) {
      logCustomAttributesNR("ca-chatbot-miaw-error", {
        errorName: error.name,
        errorStack: error.stack,
        message: error.message,
        logLevel: "error"
      });
    }
  }, []);
  return { subscribeToAgentActions, handleCloseEventSource };
};

export { useMIAWAgentActions };
//# sourceMappingURL=src-hooks-miaw-useMIAWAgentActions.js.map
